import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Alert, Box, Chip, CircularProgress, Typography } from "@mui/material";
import * as pdfjs from "pdfjs-dist";
// Bundle the worker via Vite ?url so it's served from our origin and
// pdfjs doesn't try to fetch from a CDN.
// eslint-disable-next-line import/no-unresolved
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  dateCandidates,
  findInPage,
  type MatchedBox,
  type PdfTextItem,
} from "./pdfTextMatch";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
// pdfjs v5 decodes JBig2/CCITTFax/OpenJPEG image streams via WASM and
// needs ``wasmUrl`` passed to ``getDocument`` — without it the worker
// fails on most scanned PDFs and leaves the canvas blank. The
// ``pdfjsWasm`` plugin in vite.config exposes the wasm assets here.
const WASM_URL = "/pdfjs-wasm/";

export type HighlightKind = "name" | "completion_date" | "expiration_date";

export interface Highlight {
  key: HighlightKind;
  /** Short label rendered above the box ("Name", "Completion", etc.). */
  label: string;
  /** Bedrock-extracted value. For dates, ISO; we try multiple print
   * formats internally. For names, the as-printed string. */
  value: string;
  /** Whether to treat the value as an ISO date (try multiple formats)
   * or as a verbatim string. */
  isDate?: boolean;
}

const HIGHLIGHT_COLORS: Record<HighlightKind, { stroke: string; fill: string }> = {
  name: { stroke: "rgba(33, 150, 243, 0.95)", fill: "rgba(33, 150, 243, 0.18)" },
  completion_date: {
    stroke: "rgba(76, 175, 80, 0.95)",
    fill: "rgba(76, 175, 80, 0.18)",
  },
  expiration_date: {
    stroke: "rgba(255, 152, 0, 0.95)",
    fill: "rgba(255, 152, 0, 0.18)",
  },
};

interface Props {
  url: string;
  fileName?: string;
  versionLabel?: string;
  highlights: Highlight[];
  onClose: () => void;
}

export interface HighlightingPdfViewerHandle {
  /** Scroll the named field's bounding box into view and pulse it.
   * No-op if no match was found for that field. */
  scrollToField(key: HighlightKind): void;
}

interface RenderedHighlight extends MatchedBox {
  key: HighlightKind;
  label: string;
}

interface PageData {
  pageIndex: number;
  width: number;
  height: number;
  highlights: RenderedHighlight[];
}

/**
 * PDF viewer that renders each page to a canvas and overlays
 * color-coded boxes around extracted-field text in the document. Used
 * inside the GCP cert review panel so the operator can eyeball the
 * cert and immediately see which words Bedrock pulled.
 *
 * Uses ref-imperative API (HighlightingPdfViewerHandle) for the
 * "scroll to this field" interaction — the parent triggers it from the
 * form's field-label click, viewer scrolls + pulses the box.
 */
export const HighlightingPdfViewer = forwardRef<
  HighlightingPdfViewerHandle,
  Props
>(function HighlightingPdfViewer(
  { url, fileName, versionLabel, highlights, onClose },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const boxRefs = useRef<Record<HighlightKind, HTMLDivElement | null>>({
    name: null,
    completion_date: null,
    expiration_date: null,
  });
  const [pages, setPages] = useState<PageData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Used to drive a one-shot "pulse" animation on the targeted box.
  const [pulsing, setPulsing] = useState<HighlightKind | null>(null);

  useImperativeHandle(ref, () => ({
    scrollToField(key) {
      const el = boxRefs.current[key];
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setPulsing(key);
      window.setTimeout(() => setPulsing(null), 1200);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    setPages([]);

    (async () => {
      try {
        const doc = await pdfjs.getDocument({ url, wasmUrl: WASM_URL }).promise;
        const collected: PageData[] = [];
        for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
          if (cancelled) return;
          const page = await doc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });
          const textContent = await page.getTextContent();
          // textContent.items is TextItem | TextMarkedContent — only the
          // former has str/transform/width/height we need.
          interface PdfJsTextItem {
            str: string;
            transform: number[];
            width: number;
            height: number;
          }
          const items: PdfTextItem[] = (textContent.items as unknown[])
            .filter((it: unknown): it is PdfJsTextItem => {
              if (typeof it !== "object" || it === null) return false;
              const candidate = it as { str?: unknown };
              return typeof candidate.str === "string";
            })
            .map((it) => {
              // PDF.js text items: transform[4]/transform[5] is the
              // baseline-x / baseline-y. Convert to top-left coords.
              const x = it.transform[4];
              const yBaseline = it.transform[5];
              const height = it.height || 12;
              return {
                str: it.str,
                x,
                y: viewport.height - yBaseline - height,
                width: it.width || 0,
                height,
              };
            });

          const pageHighlights: RenderedHighlight[] = [];
          for (const h of highlights) {
            const candidates = h.isDate ? dateCandidates(h.value) : [h.value];
            for (const candidate of candidates) {
              const found = findInPage(items, candidate);
              if (found) {
                pageHighlights.push({
                  key: h.key,
                  label: h.label,
                  pageIndex: pageNum - 1,
                  ...found.box,
                });
                break;
              }
            }
          }

          collected.push({
            pageIndex: pageNum - 1,
            width: viewport.width,
            height: viewport.height,
            highlights: pageHighlights,
          });

          // Render canvas immediately so the user sees progress.
          requestRender(page, viewport, pageNum - 1);
        }
        if (!cancelled) setPages(collected);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load PDF");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(highlights)]);

  // Render a single page to its canvas. Looked up by data-page-canvas
  // attribute so we don't have to hold canvas refs in state.
  const requestRender = async (
    page: Awaited<ReturnType<typeof pdfjs.getDocument>["promise"]> extends infer D
      ? D extends { getPage: (n: number) => Promise<infer P> } ? P : never
      : never,
    viewport: { width: number; height: number },
    pageIndex: number,
  ) => {
    // Defer until the canvas element exists. Pages render in order,
    // so by the time the loop reaches page N the previous render's
    // setState has flushed.
    requestAnimationFrame(() => {
      const canvas = containerRef.current?.querySelector<HTMLCanvasElement>(
        `canvas[data-page="${pageIndex}"]`,
      );
      if (!canvas) {
        // Canvas not ready yet — try once more on the next frame.
        requestAnimationFrame(() => requestRender(page, viewport, pageIndex));
        return;
      }
      const dpr = window.devicePixelRatio || 1;
      // Render at 1.5x scale for crisp rendering. The container scales
      // the canvas down via CSS to the viewport's natural width.
      const scale = 1.5;
      const scaled = (page as unknown as {
        getViewport: (opts: { scale: number }) => { width: number; height: number };
      }).getViewport({ scale: scale * dpr });
      canvas.width = scaled.width;
      canvas.height = scaled.height;
      canvas.style.width = `${viewport.width * scale}px`;
      canvas.style.height = `${viewport.height * scale}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      // pdfjs v5 RenderParameters wants both ``canvas`` and
      // ``canvasContext`` — passing only ``canvasContext`` silently
      // produces a blank canvas.
      (page as unknown as {
        render: (opts: {
          canvas: HTMLCanvasElement;
          canvasContext: CanvasRenderingContext2D;
          viewport: { width: number; height: number };
        }) => { promise: Promise<void> };
      })
        .render({ canvas, canvasContext: ctx, viewport: scaled })
        .promise.catch(() => {
          /* benign: render aborted on rapid prop change */
        });
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {fileName || "Document"}
          {versionLabel && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {versionLabel}
            </Typography>
          )}
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            border: 0,
            bgcolor: "transparent",
            cursor: "pointer",
            color: "text.secondary",
            fontSize: 13,
          }}
        >
          Hide
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box
        ref={containerRef}
        sx={{
          maxHeight: 640,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {loading && pages.length === 0 && (
          <Box sx={{ py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {pages.map((p) => {
          // The container scales the canvas (1.5x). All overlay boxes
          // need to scale the same way so they line up with the
          // rendered glyphs.
          const SCALE = 1.5;
          return (
            <Box
              key={p.pageIndex}
              sx={{
                position: "relative",
                boxShadow: 1,
              }}
              style={{ width: p.width * SCALE, height: p.height * SCALE }}
            >
              <canvas data-page={p.pageIndex} />
              {p.highlights.map((h, i) => {
                const colors = HIGHLIGHT_COLORS[h.key];
                const pulse = pulsing === h.key;
                return (
                  <Box
                    key={`${h.key}-${i}`}
                    ref={(el: HTMLDivElement | null) => {
                      boxRefs.current[h.key] = el;
                    }}
                    sx={{
                      position: "absolute",
                      border: `2px solid ${colors.stroke}`,
                      bgcolor: colors.fill,
                      borderRadius: "2px",
                      pointerEvents: "none",
                      transition: "box-shadow 0.2s ease-out",
                      boxShadow: pulse
                        ? `0 0 0 6px ${colors.fill}, 0 0 0 10px ${colors.fill}`
                        : "none",
                    }}
                    style={{
                      left: h.x * SCALE,
                      top: h.y * SCALE,
                      width: h.width * SCALE,
                      height: h.height * SCALE,
                    }}
                  >
                    <Chip
                      label={h.label}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -22,
                        left: 0,
                        height: 18,
                        fontSize: 10,
                        bgcolor: colors.stroke,
                        color: "white",
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});
